<template>
  <view-wrapper
    prefix="Print"
    name="Article"
    path="print-labels"
    :show-cancel="false"
    :loading="loading"
    loading-text="Getting article"
    back-text="Back to labels list"
  >
    <wrapper class="mb-5" :check-for-child-focused="true">
      <g-input
        type="text"
        disabled
        label-placeholder="Article name"
        v-model="article.name"
      />
    </wrapper>

    <wrapper class="mb-5" :check-for-child-focused="true">
      <div class="flex justify-between">
        <p>Label data</p>
      </div>
      <div class="label-data">
        <div
          v-for="(labelData, index) in editableLabelData"
          :key="`label-data--${index}`"
          class="grid grid-cols-5 gap-4"
        >
          <g-input
            :id="`label-key--${index}`"
            type="text"
            disabled
            :error="labelData.errors"
            label-placeholder="Key"
            v-model="labelData.name"
            :value="labelData.name"
            class="col-span-1"
          />
          <g-input
            :id="`label-data--${index}`"
            :type="labelData.propertyType"
            :readonly="!labelData.editable"
            :editable="labelData.editable"
            :maxlength="findMaxLength(labelData)"
            label-placeholder=""
            v-model="labelData.data"
            @input="updateEditableLabelData(index, labelData.name, $event)"
            :is-expandable="true"
            class="col-span-4"
          />
        </div>
      </div>
    </wrapper>

    <wrapper class="mb-5" :check-for-child-focused="true">
      <div class="flex justify-between">
        <p>Article data</p>
        <p
          class="font-bold underline cursor-pointer"
          @click="showAllArticleData = !showAllArticleData"
        >
          {{ showAllArticleData ? "Show only editable" : "Show all" }}
        </p>
      </div>

      <div class="article-data">
        <div
          v-for="(data, index) in article.articleData"
          v-show="(data.editable || showAllArticleData) && !data.isExtra"
          :key="`article-data--${index}`"
          class="grid grid-cols-5 gap-4"
        >
          <g-input
            :id="`key--${index}`"
            type="text"
            disabled
            :error="data.errors"
            label-placeholder="Key"
            v-model="data.key"
            :value="data.key"
            class="col-span-1"
          />

          <g-input
            :id="`data--${index}`"
            :type="data.propertyType"
            :readonly="!data.editable"
            :maxlength="findMaxLength(data)"
            label-placeholder=""
            v-model="data.data"
            :editable="data.editable"
            @input="updateEditableData(index, data.key, $event)"
            :is-expandable="true"
            class="col-span-4"
          />
        </div>
      </div>
    </wrapper>

    <wrapper class="mb-5" v-if="article">
      <h3>Print Article</h3>
      <printing-component
        :autoprint="autoprint"
        :presetprinter="presetPrinter"
        :articles="[article]"
        :key="componentKey"
        :connector="connectorId"
        :enable-print-to-group="article.enablePrintToGroup"
      />
    </wrapper>
  </view-wrapper>
</template>
<script>
import GInput from "../../components/forms/Input";
import PrintingComponent from "../../components/PrintingComponent";
import { getArticleDataVariables } from "../../utils/articleDataHelper";
export default {
  components: {
    GInput,
    PrintingComponent,
  },
  data: () => ({
    article: {
      name: "",
      articleData: [],
      labels: [],
      editableData: [],
      enablePrintToGroup: false,
    },
    label: {},
    loading: false,
    showAllArticleData: false,
    componentKey: 0, // To force update for modal
    loadingText: "",
    selectedPrinter: {},
    connectorId: null,
    errorText: "",
    error: "",
  }),
  computed: {
    labelId() {
      return this.$route.params.labelId;
    },
    autoprint() {
      var quantity = this.getPresetQuantity(this.$route.query);
      var autoprint = this.getAutoPrint(this.$route.query);
      return autoprint && quantity && quantity > 0;
    },
    presetPrinter() {
      return this.$route.query["grafokettonline.preset_printer"];
    },
    editableLabelData() {
      const articleVars = this.article.articleData
        .filter(ad => !ad.isExtra)
        .map(ad => ad.key);
      return (
        articleVars &&
        articleVars.length !== null &&
        this.label &&
        this.label.variables &&
        this.label.variables.filter(
          v => v.editable === true && articleVars.indexOf(v.name) == -1
        )
      );
    },
  },
  methods: {
    findMaxLength(d) {
      var k = d.key || d.name;
      var variable = this.label.variables.find(
        v => v.name.toLowerCase() === k.toLowerCase()
      );
      if (variable && variable.propertySize) {
        return variable.propertySize;
      }
      return 2000;
    },
    updateEditableLabelData(index, key, data) {
      const variable = this.label.variables.find(ed => ed.name === key);
      if (variable) {
        variable.data = data;
      }
      this.updateEditableData(index, key, data);
    },
    updateEditableData(index, key, data) {
      const variable = this.article.articleData.find(ed => ed.key === key);
      if (variable) {
        variable.data = data;
        variable.changed = true;
      }
      this.syncEditableData();
    },
    syncEditableData() {
      this.article.editableData = this.article.articleData.map(ad => ({
        key: ad.key,
        data: ad.data,
        changed: ad.changed,
      }));
    },
    async getArticleData(routeParams) {
      let url;
      if(routeParams.connectorId) {
        url = `/api/connectors/${routeParams.connectorId}/print/${routeParams.articleId}`;
      } else {
        url = routeParams.articleId
        ? `/api/articles/print/${routeParams.articleId}`
        : `/api/articles/name?name=${routeParams.articleName}`;
      }
      const response = await this.$store.$api.get(url);
      return response.data;
    },
    getPresetQuantity(routeQuery) {
      var quantity = routeQuery["grafokettonline.print_quantity"];
      if (quantity) {
        return parseInt(quantity);
      }
      return null;
    },
    getAutoPrint(routeQuery) {
      return routeQuery["grafokettonline.auto_print"] === "true";
    },
    async getData() {
      this.loadingText = "Retrieving article";

      var printQuantity = this.getPresetQuantity(this.$route.query);
      const labelResponse = await this.$store.$api.get(
        `/api/labels/${this.labelId}`
      );
      this.label = labelResponse.data.data;
      const articleResponse = await this.getArticleData(this.$route.params);
      this.article = {
        ...articleResponse.data,
        quantity: printQuantity || 1,
        articleData: getArticleDataVariables(
          [this.label],
          [],
          articleResponse.data.articleData
        ),
      };
      if (this.article.labels.length === 0) {
        let groupLabels = this.$store.getters["LABELS/LABELS"];
        if (groupLabels.length === 0) {
          await this.$store.dispatch("LABELS/GET_LABELS");
          groupLabels = this.$store.getters["LABELS/LABELS"];
        }
        this.article.labels = JSON.parse(JSON.stringify(groupLabels)); // Deep copying
      }
      this.article.labels = this.article.labels
        .filter(l => l.labelId == this.labelId)
        .map(l => ({ ...l, quantity: printQuantity }));
      this.syncEditableData();
      for (var queryParam in this.$route.query) {
        var ad = this.article.articleData.find(
          v =>
            v.editable === true &&
            v.key.toLowerCase() === queryParam.toLowerCase()
        );
        if (ad) {
          this.updateEditableData(0, ad.key, this.$route.query[queryParam]);
        }

        var ld = this.editableLabelData.find(
          v => v.name.toLowerCase() === queryParam.toLowerCase()
        );
        if (ld) {
          this.updateEditableLabelData(
            0,
            ld.name,
            this.$route.query[queryParam]
          );
        }
      }
      this.componentKey++;
    },
    setPrinter(printer) {
      this.selectedPrinter = printer;
    },
  },
  async mounted() {
    this.loading = true;
    this.connectorId = this.$route.params.connectorId;
    await this.getData();
    this.loading = false;
  },
};
</script>
